import moment from 'moment'

import twsMapping from '@/configurations/app/tws-mapping'
import codeListUtils from '@/utils/codeListUtils'
import sortUtils from '@/utils/sortUtils'
import stringUtils from '@/utils/stringUtils'
import { WATER_SAMPLE_TYPE, AIR_SAMPLE_TYPE } from '@/vuex/constants'

const SERIES_TIMESTAMP_OBSERVATION_TYPE = '87'

const DATE_FORMAT = 'YYYY-MM-DD'
const TIME_FORMAT = 'HH:mm:ss'

// Code lists that are a scale instead of a set of names. Their short code carries the order - odour
// intensity runs 0 none, 1 weak, 2 moderate, 3 strong, 4 extreme - so sorting those on the
// description would scramble the scale. The rest read as names and are sorted alphabetically.
// 9 = odour intensity, 10 = colour shade, 252 = clarity.
const ORDINAL_CODE_LIST_IDS = ['9', '10', '252']

const DEFAULT_SERIES_COUNT = 7

const SERIES_CONFIGURATION = {
  [WATER_SAMPLE_TYPE]: {
    screen: 'frmWatersampleDetails',
    fieldKeys: [
      'WsPh',
      'WsEc3',
      'WsRedox',
      'WsTemperature',
      'WsOxygen',
      'WsOxygenPercentage',
      'WsTurbidityPrePumped',
      'WsGWLafterPrepumping',
      'WsPrePumpPumpFlowRate',
      'WsColourShadeCode',
      'WsColourMainCode',
      'WsColourSubCode',
      'WsOdourGradationCode',
      'WsOdourCode',
      'WsOilWaterReaction',
      'WsClarityCode',
      'WsPrepumpFTDryCode',
      'WsSeriesMeasurementPumpedVolume',
      'WsSeriesMeasurementDuration'
    ]
  },
  [AIR_SAMPLE_TYPE]: {
    screen: 'frmAirsampleDetails',
    fieldKeys: [
      'WsTemperature',
      'WsOxygen',
      'WsOxygenPercentage',
      'WsPrePumpPumpFlowRate',
      'WsAirPressure',
      'WsCarbonDioxine',
      'WsHumidityPercentage',
      'WsPid',
      'WsOdourGradationCode',
      'WsOdourCode',
      'WsLowerExplosiveLevel',
      'WsH2S',
      'WsCOppm',
      'WsCH4ppm',
      'WsVacuum'
    ]
  }
}

function getConfiguration (sampleType) {
  return SERIES_CONFIGURATION[sampleType] || SERIES_CONFIGURATION[WATER_SAMPLE_TYPE]
}

function getSeriesMapping (fieldKey, sampleType) {
  if (!getConfiguration(sampleType).fieldKeys.includes(fieldKey)) return null

  return twsMapping.observation.find(entry => entry.key === fieldKey && entry.seriesType) || null
}

function getControlLabel (control, languageCode) {
  const label = (control.labels || []).find(item => item[languageCode])

  return label ? label[languageCode] : ''
}

// An option carries one entry per language for its description and for its short code; merge them so
// the one for the requested language can be picked whatever order the backend returned them in.
function getLocalizedValue (entries, languageCode) {
  const values = {}
  ;(Array.isArray(entries) ? entries : []).forEach(entry => Object.assign(values, entry))

  return values[languageCode] || ''
}

function toOptions (field, languageCode) {
  if (!field.options) return []

  const sortsOnCode = ORDINAL_CODE_LIST_IDS.includes(String(field.codeListLookupId))

  return codeListUtils
    .getVisibleCodesForField(field)
    .map(option => {
      return {
        value: option.value,
        text: getLocalizedValue(option.text, languageCode),
        code: getLocalizedValue(option.codeInterface, languageCode)
      }
    })
    .sort((first, second) =>
      sortsOnCode ? sortUtils.naturalSort(first.code, second.code) : sortUtils.naturalSort(first.text, second.text)
    )
}

function toTimerValue (observation) {
  return parseInt(observation.ObSerieTimerValue, 10)
}

// parseZone keeps the offset the observation was stored with, so the time reads as the field worker
// wrote it down instead of being moved into the reader's time zone.
function formatStoredDate (dateTime, format) {
  if (stringUtils.isNullOrEmptyOrWhitespace(dateTime)) return ''

  const parsed = moment.parseZone(dateTime)

  return parsed.isValid() ? parsed.format(format) : ''
}

function getTimePart (observationDate) {
  return formatStoredDate(observationDate, TIME_FORMAT)
}

function getDatePart (dateTime) {
  return formatStoredDate(dateTime, DATE_FORMAT)
}

function hasValue (entry, fieldKey) {
  return !stringUtils.isNullOrEmptyOrWhitespace(entry.values[fieldKey])
}

function hasMeasurements (entry, seriesFields) {
  return seriesFields.some(seriesField => hasValue(entry, seriesField.key))
}

function buildObservationDate (entry, waterSample) {
  if (!entry.time) return ''

  const date = getDatePart(entry.timeObservationDate) || getDatePart(waterSample.WsDate) || moment().format(DATE_FORMAT)

  return `${date}T${entry.time}`
}

function buildObservation (entry, waterSample, projectId, observationType, observationId) {
  return {
    ObID: observationId,
    PrID: projectId,
    WsID: waterSample.WsID,
    WsGuid: waterSample.WsGuid,
    ObObservationType: observationType,
    ObSerieTimerValue: entry.ObSerieTimerValue
  }
}

export default {
  getSeriesScreen (sampleType) {
    return getConfiguration(sampleType).screen
  },

  getSeriesFields (template, language, sampleType) {
    if (!template || !template.controls) return []

    const languageCode = stringUtils.convert2To3LetterLanguageCode(language)
    const seriesFields = []

    template.controls
      .slice()
      .sort((first, second) => parseInt(first.order, 10) - parseInt(second.order, 10))
      .forEach(control => {
        const controlFields = []

        ;(control.fields || []).forEach(field => {
          const mapping = getSeriesMapping(field.key, sampleType)
          if (!mapping) return
          if (seriesFields.concat(controlFields).some(seriesField => seriesField.key === field.key)) return

          const isCode = mapping.field === 'ObPropertyCode'

          controlFields.push({
            key: field.key,
            seriesType: mapping.seriesType,
            observationField: mapping.field,
            unit: mapping.unit,
            label: getControlLabel(control, languageCode),
            labelTranslationKey: `label.seriesField.${field.key}`,
            isCode,
            options: isCode ? toOptions(field, languageCode) : []
          })
        })

        // One control can hold several fields that are measured as a series - colour holds three -
        // and they would all end up under the same control label. Only those need a name of their
        // own; the rest read like the field in the editor panel next to the table.
        controlFields.forEach(seriesField => {
          seriesField.sharesControlLabel = controlFields.length > 1
        })

        seriesFields.push(...controlFields)
      })

    return seriesFields
  },

  buildSeries (observations, waterSample, seriesFields, seriesCount = DEFAULT_SERIES_COUNT) {
    const sampleObservations = (observations || []).filter(
      observation => observation.WsID === waterSample.WsID && toTimerValue(observation) > 0
    )

    const timerValues = []
    for (let timerValue = 1; timerValue <= seriesCount; timerValue++) {
      timerValues.push(timerValue)
    }
    sampleObservations.forEach(observation => {
      const timerValue = toTimerValue(observation)
      if (!timerValues.includes(timerValue)) timerValues.push(timerValue)
    })
    timerValues.sort((first, second) => first - second)

    return timerValues.map(timerValue => {
      const entryObservations = sampleObservations.filter(observation => toTimerValue(observation) === timerValue)
      const timeObservation =
        entryObservations.find(observation => observation.ObObservationType === SERIES_TIMESTAMP_OBSERVATION_TYPE) || {}

      const entry = {
        ObSerieTimerValue: timerValue,
        time: getTimePart(timeObservation.ObObservationDate),
        timeObservationDate: timeObservation.ObObservationDate || '',
        timeObservationId: timeObservation.ObID,
        values: {},
        observationIds: {}
      }

      seriesFields.forEach(seriesField => {
        const observation = entryObservations.find(item => item.ObObservationType === seriesField.seriesType)
        if (!observation) return

        entry.values[seriesField.key] = observation[seriesField.observationField] || ''
        entry.observationIds[seriesField.key] = observation.ObID
      })

      return entry
    })
  },

  hasMeasurements (entry, seriesFields) {
    return hasMeasurements(entry, seriesFields)
  },

  getActiveTimerValue (series, seriesFields) {
    if (!series.length) return null

    const filledEntries = series.filter(entry => hasMeasurements(entry, seriesFields))
    if (!filledEntries.length) return series[0].ObSerieTimerValue

    return Math.max(...filledEntries.map(entry => entry.ObSerieTimerValue))
  },

  isLocked (series, entry, seriesField) {
    if (!series.length || entry.ObSerieTimerValue === series[0].ObSerieTimerValue) return false

    const filledEntries = series.filter(item => hasValue(item, seriesField.key))
    if (!filledEntries.length) return true

    const lastFilledTimerValue = Math.max(...filledEntries.map(item => item.ObSerieTimerValue))

    return entry.ObSerieTimerValue - 1 > lastFilledTimerValue
  },

  getSeriesFieldKeys (series, seriesFields) {
    return seriesFields
      .filter(seriesField =>
        series.some(entry => hasValue(entry, seriesField.key) || entry.observationIds[seriesField.key])
      )
      .map(seriesField => seriesField.key)
  },

  getStableValues (series, seriesFields, fieldKeys) {
    const stableValues = {}

    seriesFields
      .filter(seriesField => fieldKeys.includes(seriesField.key))
      .forEach(seriesField => {
        const filledEntries = series.filter(entry => hasValue(entry, seriesField.key))
        const lastFilledEntry = filledEntries[filledEntries.length - 1]

        stableValues[seriesField.key] = lastFilledEntry ? lastFilledEntry.values[seriesField.key] : ''
      })

    return stableValues
  },

  applyTimeStamp (entry, seriesFields, now = new Date()) {
    if (!hasMeasurements(entry, seriesFields)) {
      entry.time = ''
      entry.timeObservationDate = ''

      return entry
    }

    if (entry.time) return entry

    entry.time = moment(now).format(TIME_FORMAT)
    entry.timeObservationDate = moment(now).format(`${DATE_FORMAT}[T]${TIME_FORMAT}`)

    return entry
  },

  toObservations (series, seriesFields, waterSample, projectId) {
    const observations = []

    series.forEach(entry => {
      seriesFields.forEach(seriesField => {
        const value = entry.values[seriesField.key]
        const observationId = entry.observationIds[seriesField.key]
        if (!observationId && (value === undefined || value === '')) return

        const observation = buildObservation(entry, waterSample, projectId, seriesField.seriesType, observationId)

        if (seriesField.unit !== '-1') observation.ObMeasurementUnit = seriesField.unit
        observation[seriesField.observationField] = value === undefined ? '' : value

        observations.push(observation)
      })

      if (!entry.timeObservationId && !entry.time) return

      const timeObservation = buildObservation(
        entry,
        waterSample,
        projectId,
        SERIES_TIMESTAMP_OBSERVATION_TYPE,
        entry.timeObservationId
      )
      timeObservation.ObObservationDate = buildObservationDate(entry, waterSample)

      observations.push(timeObservation)
    })

    return observations
  }
}
